import {damsFetch} from "../app/damsFetch";

/**
 * DE-1723 (ADR-0011): a share is made in the background after the backend accepted it. Its outcome is asked for every
 * 2 seconds, for up to 2 minutes, with short requests.
 */

const DONE = new Set(['sent', 'waiting']);

/**
 * Asks for an accepted share's outcome until it is known.
 *
 * @param {string} sharingUuid - The share, as the backend accepted it.
 * @param {Object} [options] - Injectable for tests: fetchStatus, sleep, now, interval, timeout.
 * @returns {Promise<'sent'|'waiting'|'pending'>} pending when the share was still being made at the timeout.
 * @throws {Error} When the share failed.
 */
export const pollShareStatus = async (sharingUuid, options) => {
    const {fetchStatus, sleep, now, interval, timeout} = {..._defaults(), ...options};
    const deadline = now() + timeout;
    for (;;) {
        await sleep(interval);
        const status = await fetchStatus(sharingUuid).catch(() => undefined);
        if (status === 'failed') throw new Error('sharing failed');
        if (DONE.has(status)) return status;
        if (now() >= deadline) return 'pending';
    }
};

/**
 * The backend's view of a share: sending, sent, waiting or failed.
 *
 * @param {string} sharingUuid
 * @returns {Promise<string|undefined>}
 */
export const fetchShareStatus = async (sharingUuid) => {
    const result = await damsFetch(`/share/${sharingUuid}/status`, {headers: {'correlation-id': crypto.randomUUID()}});
    return result?.status;
};

const _defaults = () => ({
    fetchStatus: fetchShareStatus,
    sleep: (ms) => new Promise(resolve => setTimeout(resolve, ms)),
    now: Date.now,
    interval: 2000,
    timeout: 120000,
});
